import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import SellerLayout from "@/components/SellerLayout";
import SearchInput from "@/components/SearchInput";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, MapPinned, Edit, Trash2, Loader2, CheckCircle, XCircle } from "lucide-react";

// Extracted LocationForm component to prevent cursor jumping
const LocationForm = ({ 
  onSubmit, 
  submitText, 
  formData, 
  setFormData, 
  pincodeLoading,
  lookupPincode 
}) => (
  <form onSubmit={onSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
    {/* Company Details */}
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label htmlFor="company_name">Company Name *</Label>
        <Input
          id="company_name"
          value={formData.company_name}
          onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
          required
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="contact_person_name">Contact Person *</Label>
        <Input
          id="contact_person_name"
          value={formData.contact_person_name}
          onChange={(e) => setFormData({ ...formData, contact_person_name: e.target.value })}
          required
        />
      </div>
    </div>

    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label htmlFor="drop_contact">Contact Number *</Label>
        <Input
          id="drop_contact"
          value={formData.drop_contact}
          onChange={(e) => setFormData({ ...formData, drop_contact: e.target.value.replace(/\D/g, "").slice(0, 10) })}
          maxLength={10}
          inputMode="numeric"
          placeholder="10-digit number"
          required
        />
        {formData.drop_contact && formData.drop_contact.length !== 10 && (
          <p className="text-xs text-red-600">Enter a valid 10-digit phone number (required for the Delhivery manifest).</p>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
        />
      </div>
    </div>

    {/* Address Section */}
    <div className="border-t pt-4 mt-4">
      <h4 className="font-semibold mb-3">Drop Address</h4>
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="address_line">Address Line *</Label>
          <Input
            id="address_line"
            value={formData.address_line}
            onChange={(e) => setFormData({ ...formData, address_line: e.target.value })}
            required
          />
        </div>
        <div className="grid grid-cols-4 gap-4">
          <div className="space-y-2">
            <Label htmlFor="country">Country *</Label>
            <Input
              id="country"
              value={formData.country}
              onChange={(e) => setFormData({ ...formData, country: e.target.value })}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="pincode">Pincode *</Label>
            <div className="relative">
              <Input
                id="pincode"
                value={formData.pincode}
                maxLength={6}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setFormData({ ...formData, pincode: value });
                  if (value.length === 6) {
                    lookupPincode(value);
                  }
                }}
                inputMode="numeric"
                required
              />
              {pincodeLoading && (
                <Loader2 className="absolute right-3 top-3 w-4 h-4 animate-spin" />
              )}
            </div>
            {formData.pincode && formData.pincode.length !== 6 && (
              <p className="text-xs text-red-600">Pincode must be 6 digits.</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="city">City *</Label>
            <Input
              id="city"
              value={formData.city}
              onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              required
              readOnly={pincodeLoading}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="state">State *</Label>
            <Input
              id="state"
              value={formData.state}
              onChange={(e) => setFormData({ ...formData, state: e.target.value })}
              required
              readOnly={pincodeLoading}
            />
          </div>
        </div>
      </div>
    </div>

    <Button type="submit" className="w-full" style={{backgroundColor: '#00A699'}}>
      {submitText}
    </Button>
  </form>
);

const DropLocations = ({ user, onLogout }) => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [pincodeLoading, setPincodeLoading] = useState(false);

  const [formData, setFormData] = useState({
    seller_id: user.seller_id || "",
    company_name: "",
    contact_person_name: "",
    drop_contact: "",
    email: "",
    address_line: "",
    country: "India",
    pincode: "",
    city: "",
    state: "",
  });

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async (query = "") => {
    try {
      const url = query ? `${API}/drop-locations/?q=${encodeURIComponent(query)}` : `${API}/drop-locations/`;
      const response = await axios.get(url);
      setLocations(response.data);
    } catch (error) {
      toast.error("Failed to fetch drop locations");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (query) => {
    fetchLocations(query);
  };

  const lookupPincode = async (pincode) => {
    if (pincode.length !== 6) return;

    setPincodeLoading(true);

    try {
      const response = await axios.get(`${API}/pickup-locations/pincode/${pincode}`);
      setFormData((prev) => ({
        ...prev,
        pincode: pincode,
        city: response.data.city,
        state: response.data.state,
      }));
      toast.success("Pincode verified!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid pincode");
    } finally {
      setPincodeLoading(false);
    }
  };

  const handleCreateLocation = async (e) => {
    e.preventDefault();
    if (!/^\d{10}$/.test(formData.drop_contact || "")) { toast.error("Enter a valid 10-digit contact number."); return; }
    if (!/^\d{6}$/.test(formData.pincode || "")) { toast.error("Pincode must be 6 digits."); return; }
    try {
      await axios.post(`${API}/drop-locations/`, formData);
      toast.success("Drop location created successfully!");
      setShowCreateDialog(false);
      resetForm();
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create drop location");
    }
  };

  const handleUpdateLocation = async (e) => {
    e.preventDefault();
    if (!/^\d{10}$/.test(formData.drop_contact || "")) { toast.error("Enter a valid 10-digit contact number."); return; }
    if (!/^\d{6}$/.test(formData.pincode || "")) { toast.error("Pincode must be 6 digits."); return; }
    try {
      const payload = { ...formData };
      delete payload.seller_id;
      await axios.put(`${API}/drop-locations/${selectedLocation.id}`, payload);
      toast.success("Drop location updated successfully!");
      setShowEditDialog(false);
      setSelectedLocation(null);
      resetForm();
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update drop location");
    }
  };

  const handleToggleStatus = async (locationId, currentStatus) => {
    const action = currentStatus ? "disable" : "enable";
    if (!window.confirm(`Are you sure you want to ${action} this drop location?`)) return;

    try {
      const response = await axios.patch(`${API}/drop-locations/${locationId}/toggle-status`);
      toast.success(response.data.message);
      fetchLocations();
    } catch (error) {
      toast.error("Failed to update drop location status");
    }
  };

  const resetForm = () => {
    setFormData({
      seller_id: user.seller_id || "",
      company_name: "",
      contact_person_name: "",
      drop_contact: "",
      email: "",
      address_line: "",
      country: "India",
      pincode: "",
      city: "",
      state: "",
    });
  };

  const openEditDialog = (location) => {
    setSelectedLocation(location);
    setFormData({
      seller_id: location.seller_id,
      company_name: location.company_name,
      contact_person_name: location.contact_person_name || "",
      drop_contact: location.drop_contact,
      email: location.email || "",
      address_line: location.address_line,
      country: location.country || "India",
      pincode: location.pincode,
      city: location.city,
      state: location.state,
    });
    setShowEditDialog(true);
  };

  return (
    <SellerLayout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold text-slate-800 mb-2">Drop Locations</h1>
            <p className="text-slate-600">Manage your drop locations</p>
          </div>
          <div className="flex gap-4 items-center">
            <SearchInput 
              onSearch={handleSearch}
              placeholder="Search locations..."
            />
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button style={{backgroundColor: '#00A699'}} onClick={() => resetForm()}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Drop Location
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl">
                <DialogHeader>
                  <DialogTitle>Add Drop Location</DialogTitle>
                  <DialogDescription>Create a new drop location for your organization</DialogDescription>
                </DialogHeader>
                <LocationForm 
                  onSubmit={handleCreateLocation} 
                  submitText="Create Location"
                  formData={formData}
                  setFormData={setFormData}
                  pincodeLoading={pincodeLoading}
                  lookupPincode={lookupPincode}
                />
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <Card key={i} className="animate-pulse">
                <CardHeader>
                  <div className="h-4 bg-slate-200 rounded w-32"></div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="h-3 bg-slate-200 rounded w-full"></div>
                    <div className="h-3 bg-slate-200 rounded w-3/4"></div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : locations.length === 0 ? (
          <Card className="border-0 shadow-lg">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <MapPinned className="w-16 h-16 text-slate-300 mb-4" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">No drop locations yet</h3>
              <p className="text-slate-500 mb-6">Add your first drop location to get started</p>
              <Button style={{backgroundColor: '#00A699'}} onClick={() => setShowCreateDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add Drop Location
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Location ID</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Company</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Contact</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Address</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {locations.map((location) => (
                    <tr key={location.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 align-middle">
                        <span className="text-sm font-mono text-blue-600 font-medium">{location.location_id}</span>
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <span className="text-sm font-medium text-slate-900">{location.company_name}</span>
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <div>
                          <p className="text-sm text-slate-900">{location.contact_person_name}</p>
                          <p className="text-xs text-slate-500">{location.drop_contact}</p>
                          {location.email && <p className="text-xs text-slate-400">{location.email}</p>}
                        </div>
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <div className="max-w-xs">
                          <p className="text-sm text-slate-700 truncate">{location.address_line}</p>
                          <p className="text-xs text-slate-500">{location.city}, {location.state} - {location.pincode}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 align-middle whitespace-nowrap">
                        <Badge className={location.is_active ? "bg-green-100 text-green-800 border-0" : "bg-red-100 text-red-800 border-0"}>
                          {location.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 align-middle whitespace-nowrap">
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEditDialog(location)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className={location.is_active ? "text-orange-600 hover:text-orange-700" : "text-green-600 hover:text-green-700"}
                            onClick={() => handleToggleStatus(location.id, location.is_active)}
                            title={location.is_active ? "Disable" : "Enable"}
                          >
                            {location.is_active ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Edit Drop Location</DialogTitle>
            <DialogDescription>Update drop location details</DialogDescription>
          </DialogHeader>
          <LocationForm 
            onSubmit={handleUpdateLocation} 
            submitText="Update Location"
            formData={formData}
            setFormData={setFormData}
            pincodeLoading={pincodeLoading}
            lookupPincode={lookupPincode}
          />
        </DialogContent>
      </Dialog>
    </SellerLayout>
  );
};

export default DropLocations;
